import { showCreateTokenState } from '@/atoms';
import { useCreateToken } from '@/services/tokens';
import { Button, Group, Input, InputLabel, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { ModalLayout } from 'moh-ui';
import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useRecoilState } from 'recoil-next';

export default function CreateTokenModal() {
   const [show, setShow] = useRecoilState(showCreateTokenState);
   const [owner, setOwner] = useState('');
   const [description, setDescription] = useState('');
   const [origin, setOrigin] = useState('');
   const [errors, setErrors] = useState({ owner: '', description: '', origin: '' });
   const queryClient = useQueryClient();
   const { mutate: createToken, isLoading } = useCreateToken();

   const handleClose = () => {
      setShow(false);
      setOwner('');
      setDescription('');
      setOrigin('');
      setErrors({ owner: '', description: '', origin: '' });
   };

   const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      let hasErrors = false;
      const newErrors = { owner: '', description: '', origin: '' };

      if (!owner.trim()) {
         newErrors.owner = 'Owner is required';
         hasErrors = true;
      }
      if (!description.trim()) {
         newErrors.description = 'Description is required';
         hasErrors = true;
      }
      if (!origin.trim()) {
         newErrors.origin = 'Origin is required';
         hasErrors = true;
      }

      setErrors(newErrors);

      if (!hasErrors) {
         createToken(
            { owner, description, origin },
            {
               onSuccess: () => {
                  queryClient.invalidateQueries('tokens');
                  showNotification({
                     title: 'Success',
                     message: 'Token created successfully',
                     color: 'green',
                  });
                  handleClose();
               },
               onError: (error) => {
                  showNotification({
                     title: 'Error',
                     message: error.message || 'Failed to create token',
                     color: 'red',
                  });
               },
            },
         );
      }
   };

   return (
      <ModalLayout open={show} onClose={handleClose} size="md">
         <h2 className="text-xl font-semibold">Create Token</h2>
         <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
               <InputLabel required>Owner</InputLabel>
               <Input
                  value={owner}
                  onChange={(e) => setOwner(e.target.value)}
                  placeholder="Enter owner"
                  disabled={isLoading}
                  error={errors.owner}
               />
               {errors.owner && (
                  <Text color="red" size="sm">
                     {errors.owner}
                  </Text>
               )}
            </div>
            <div className="space-y-2">
               <InputLabel required>Description</InputLabel>
               <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter description"
                  disabled={isLoading}
                  error={errors.description}
               />
               {errors.description && (
                  <Text color="red" size="sm">
                     {errors.description}
                  </Text>
               )}
            </div>
            <div className="space-y-2">
               <InputLabel required>Origin</InputLabel>
               <Input
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                  placeholder="Enter origin"
                  disabled={isLoading}
                  error={errors.origin}
               />
               {errors.origin && (
                  <Text color="red" size="sm">
                     {errors.origin}
                  </Text>
               )}
            </div>
            <Group mt="md">
               <Button variant="outline" onClick={handleClose} disabled={isLoading}>
                  Cancel
               </Button>
               <Button type="submit" loading={isLoading}>
                  Create Token
               </Button>
            </Group>
         </form>
      </ModalLayout>
   );
}
